import { AppBlock } from '@boost/widget-integration-tae/lib';
import { Module } from 'framework/core';

import { DomHelper } from '../../helpers/dom.helper';
import { WCAGHelper } from '../../helpers/wcag.helper';
import { AppService } from '../app/app.service';
import { PlatformModule } from '../platform/platform.module';
import { CartAPI } from './cart.api';
import { CartController } from './cart.controller';
import { CartLifecycle } from './cart.lifecycle';
import { CartSelectors } from './cart.selectors';
import { CartService } from './cart.service';
import { CartDrawerRecommendationConstants } from './cart-drawer-recommendation/constants';
import { CartDrawerRecommendationController } from './cart-drawer-recommendation/controller';
import { CartDrawerRecommendationLifecycle } from './cart-drawer-recommendation/lifecycle';
import { CartDrawerRecommendationService } from './cart-drawer-recommendation/service';

/**
 * Cart module managing cart functionality via Widget Integration framework
 *
 * This module orchestrates cart operations including:
 * - Cart modal display and interactions
 * - Add to cart functionality
 * - Cart state management
 * - Theme-specific cart icon handling
 *
 * Technical Support teams can extend this module for shop-specific customization:
 * - Override block listener setup via {@link setupBlockListener}
 * - Customize block initialization via {@link onInitBlock}
 * - Add custom error handling via {@link handleBlockInitError}
 *
 * @example
 * ```javascript
 * // Extend CartModule for custom initialization
 * window.boostWidgetIntegration.extend('CartModule', (CartModule) => {
 *   return class CustomCartModule extends CartModule {
 *     setupBlockListener() {
 *       super.setupBlockListener();
 *       // Add custom cart event handling
 *       this.registerCustomEventHandlers();
 *     }
 *
 *     async onInitBlock(block) {
 *       // Add shop-specific tracking before initialization
 *       this.trackCartBlockInit(block.id);
 *       await super.onInitBlock(block);
 *     }
 *
 *     registerCustomEventHandlers() {
 *       window.addEventListener('custom-cart-event', () => {
 *         this.controller?.openCart();
 *       });
 *     }
 *
 *     trackCartBlockInit(blockId) {
 *       window.dataLayer?.push({
 *         event: 'cart_block_initialized',
 *         blockId
 *       });
 *     }
 *   };
 * });
 * ```
 */
@Module({
  imports: [PlatformModule],
  providers: [
    CartLifecycle,
    CartDrawerRecommendationConstants,
    CartDrawerRecommendationLifecycle,
    CartDrawerRecommendationService,
    CartService,
    CartAPI,
    CartSelectors,
    CartController,
    WCAGHelper,
    DomHelper,
    CartDrawerRecommendationController,
  ],
  exports: [
    CartDrawerRecommendationService,
    CartService,
    CartAPI,
    CartSelectors,
    CartController,
    CartDrawerRecommendationController,
  ],
})
export class CartModule {
  protected readonly cartBlockListener = (block: AppBlock): void => {
    if (block.blockType === 'cart') {
      void this.onInitBlock(block);
    }
  };

  constructor(
    protected appService: AppService,
    protected cartLifecycle: CartLifecycle,
    protected cartDrawerRecommendationLifecycle: CartDrawerRecommendationLifecycle
  ) {
    this.setupBlockListener();
  }

  get controller(): CartController | null {
    return this.cartLifecycle.controller;
  }

  get cartDrawerRecommendationController(): CartDrawerRecommendationController | null {
    return this.cartDrawerRecommendationLifecycle.controller;
  }

  get cartSelectors(): CartSelectors | null {
    return this.cartLifecycle.selectors;
  }

  /**
   * Sets up the TAE App block listener for cart blocks.
   * Override this method to customize block registration logic or support additional block types.
   *
   * @example
   * ```javascript
   * // Add support for custom cart block types
   * protected setupBlockListener() {
   *   this.appService.TAEApp.addRegistryBlockListener((block) => {
   *     if (block.blockType === 'cart' || block.blockType === 'mini-cart') {
   *       this.onInitBlock(block);
   *     }
   *   });
   * }
   * ```
   */
  protected setupBlockListener(): void {
    try {
      const taeApp = this.appService?.TAEApp;
      if (!taeApp || typeof taeApp.addRegistryBlockListener !== 'function') {
        console.warn('TAEApp not ready for cart block listener registration');
        return;
      }

      taeApp.addRegistryBlockListener(this.cartBlockListener);
    } catch (error) {
      console.error('Failed to setup cart block listener:', error);
    }
  }

  /**
   * Called when a cart block is initialized.
   * Override this method to customize cart initialization or add shop-specific setup logic.
   *
   * @param block - The AppBlock instance being initialized
   *
   * @example
   * ```javascript
   * // Add shop-specific initialization
   * protected async onInitBlock(block) {
   *   // Add custom tracking before cart initialization
   *   this.trackCartInit(block.id);
   *
   *   // Initialize cart with custom configuration
   *   await super.onInitBlock(block);
   *
   *   // Add post-initialization custom logic
   *   this.setupCustomCartFeatures(block);
   * }
   * ```
   */
  protected async onInitBlock(block: AppBlock): Promise<void> {
    try {
      await this.initializeCart(block);
      await this.initializeCartDrawerRecommendation(block);
    } catch (error) {
      this.handleBlockInitError(error as Error, block);
    }
  }

  protected async initializeCart(block: AppBlock): Promise<void> {
    await this.cartLifecycle.initialize(block);
  }

  protected async initializeCartDrawerRecommendation(block: AppBlock): Promise<void> {
    await this.cartDrawerRecommendationLifecycle.initialize(block);
  }

  /**
   * Handles errors that occur during cart block initialization.
   * Override this method to customize error handling, add custom logging, or provide user feedback.
   *
   * @param error - The error that occurred
   * @param block - The AppBlock that failed to initialize
   *
   * @example
   * ```javascript
   * // Add custom error handling and user notification
   * protected handleBlockInitError(error, block) {
   *   // Log to custom analytics service
   *   this.analyticsService.trackError('cart_init_failed', {
   *     blockId: block.id,
   *     errorMessage: error.message,
   *     errorStack: error.stack,
   *   });
   *
   *   // Call parent error handler
   *   super.handleBlockInitError(error, block);
   *
   * // Show user-friendly error message
   *   this.showCartErrorNotification('Unable to load cart. Please refresh the page.');
   * }
   * ```
   */
  protected handleBlockInitError(error: Error, block: AppBlock): void {
    console.error(`Failed to initialize cart block ${block.id}:`, error);
  }

  destroy(): void {
    const taeApp = this.appService?.TAEApp;
    if (taeApp && typeof taeApp.removeRegistryBlockListener === 'function') {
      taeApp.removeRegistryBlockListener(this.cartBlockListener);
    }

    this.cartLifecycle.destroy();
    this.cartDrawerRecommendationLifecycle.destroy();
  }
}
